/**
 * A button's QR code as an SVG file to print: the code of its address, the button's title below it
 * (docs/plans/buttons-and-place-lists.md, part 2). Black on white with a quiet zone, a system font stack.
 */
import QRCode from "qrcode";

const SIZE = 320; // the code, in SVG units
const PAD = 24; // the quiet zone around it
const LINE = 26; // one line of the title
const CHARS = 26; // characters per line, about, at 20 units
const FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

function escapeXml(s: string): string {
  return s.replace(
    /[<>&"']/g,
    (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" })[c]!,
  );
}

/** At most two lines, broken between words; a longer title ends with "…". */
export function titleLines(title: string): string[] {
  const words = title.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= CHARS || !cur) {
      cur = next;
      continue;
    }
    lines.push(cur);
    cur = w;
  }
  if (cur) lines.push(cur);
  if (lines.length <= 2) return lines.map((l) => (l.length > CHARS ? `${l.slice(0, CHARS - 1)}…` : l));
  const second = lines.slice(1).join(" ");
  return [lines[0]!.slice(0, CHARS), `${second.slice(0, CHARS - 1)}…`];
}

/** The SVG: the code of `url` and `title` below it. */
export async function qrSvg(url: string, title: string): Promise<string> {
  const code = await QRCode.toString(url, { type: "svg", margin: 0, errorCorrectionLevel: "M" });
  const placed = code.trim().replace("<svg ", `<svg x="${PAD}" y="${PAD}" width="${SIZE}" height="${SIZE}" `);
  const lines = titleLines(title);
  const width = SIZE + 2 * PAD;
  const height = SIZE + 2 * PAD + (lines.length ? lines.length * LINE + 8 : 0);
  const text = lines
    .map(
      (l, i) =>
        `<text x="${width / 2}" y="${SIZE + PAD + 22 + i * LINE}" text-anchor="middle" font-family="${FONT}" ` +
        `font-size="20" fill="#000000">${escapeXml(l)}</text>`,
    )
    .join("");
  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
    `<title>${escapeXml(title)}</title><rect width="100%" height="100%" fill="#ffffff"/>${placed}${text}</svg>\n`
  );
}

/** A file name from the title: `Washing machine` → `washing-machine.svg`. */
export function qrFileName(title: string): string {
  const base = title
    .toLowerCase()
    .normalize("NFC")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/, "");
  return `${base || "button"}.svg`;
}

/** Make the SVG and hand it to the browser as a download. */
export async function downloadQr(url: string, title: string): Promise<void> {
  const blob = new Blob([await qrSvg(url, title)], { type: "image/svg+xml" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = qrFileName(title);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
